import React, { useState, useEffect, useMemo, Suspense } from "react";
import { Modal, Button, Tooltip } from "antd";
import {
  FullscreenOutlined,
  WarningOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import Loading from "components/shared-components/Loading";
import { getAppModule } from "configs/appModules";
import { goToBugURL, setOpenAppContext, clearOpenAppContext } from "views/app-views/apps/commons";
import "./index.css";

// Alturas de header/footer (deben coincidir con el CSS)
const HEADER_HEIGHT = 72;
const FOOTER_HEIGHT = 64;

// Deriva un título legible desde la ruta cuando no llega uno explícito
function titleFromPath(path) {
  if (!path) return "Actividad";
  const last = path.split("/").filter(Boolean).pop() || "Actividad";
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

/**
 * Normaliza el campo `Tutorial` de la actividad a una URL abrible en pestaña
 * nueva. Se aceptan URL absoluta, protocolo relativo (//host) y ruta del propio
 * hosting (/guias/x.pdf); cualquier otro esquema (javascript:, data:, …) se
 * descarta para que un dato mal cargado no llegue a window.open. Un valor sin
 * protocolo se asume dominio y se completa con https.
 * @param {*} raw Valor del campo Tutorial en la actividad
 * @returns {string|null} URL utilizable, o null si no hay tutorial válido
 */
function tutorialUrl(raw) {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return null;
  const lower = value.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return value;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  return `https://${value}`;
}

/**
 * AppPopup — Modal global que monta un módulo de `appModules` sin cambiar de
 * ruta, para no perder el contexto de la plataforma que quedó por debajo.
 *
 * Se abre despachando un evento de window:
 *   window.dispatchEvent(new CustomEvent('openApp', { detail: { path, title } }))
 * (mismo patrón que BugReportModal con 'openBugReport').
 *
 * Dos marcos, según el `kind` de la entrada en appModules:
 *   - "unity" (por defecto): header oscuro + stage 16:9 + footer con acciones.
 *     El modal se dimensiona al ancho del lienzo (sin franjas negras laterales)
 *     y se cierra con confirmación, porque se pierde el progreso interno.
 *     Una sola instancia Unity viva: modal único + destroyOnClose.
 *   - "tool": modal claro corriente para pantallas de administración (sin
 *     lienzo, sin pantalla completa, alto automático con scroll interno).
 */
export default function AppPopup() {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState(null);
  const [title, setTitle] = useState("");
  // URL del tutorial de la actividad abierta (campo `Tutorial`); sin ella el
  // botón de ayuda queda deshabilitado.
  const [tutorial, setTutorial] = useState(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Entrada de appModules que corresponde a la ruta abierta
  const appModule = useMemo(() => (path ? getAppModule(path) : null), [path]);
  const isTool = appModule?.kind === "tool";

  // Escucha el evento de apertura
  useEffect(() => {
    const handler = (e) => {
      const nextPath = e?.detail?.path;
      const mod = nextPath ? getAppModule(nextPath) : null;
      if (!mod) return;
      // Quita el foco del canvas actual (evita que Unity siga capturando teclas)
      document.querySelectorAll("canvas").forEach((c) => c.blur());
      const nextTitle = e?.detail?.title || mod.title || titleFromPath(nextPath);
      setPath(nextPath);
      setTitle(nextTitle);
      setTutorial(tutorialUrl(e?.detail?.tutorial));
      setOpen(true);
      // El módulo se monta sin cambiar la ruta, así que se registra aparte para
      // que "Reportar Error" sepa qué simulador estaba abierto.
      setOpenAppContext(nextPath, nextTitle);
    };
    window.addEventListener("openApp", handler);
    return () => window.removeEventListener("openApp", handler);
  }, []);

  // Calcula el mayor lienzo 16:9 que cabe (dejando espacio para header + footer)
  useEffect(() => {
    if (!open || isTool) return undefined;
    const compute = () => {
      const maxW = window.innerWidth * 0.96;
      const maxH = window.innerHeight * 0.94;
      const availH = Math.max(0, maxH - HEADER_HEIGHT - FOOTER_HEIGHT);
      const w = Math.min(maxW, (availH * 16) / 9);
      const h = (w * 9) / 16;
      setDims({ w, h });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open, isTool]);

  // Resuelve el componente lazy para la ruta actual
  const LazyApp = useMemo(
    () => (appModule ? React.lazy(appModule.importer) : null),
    [appModule]
  );

  const body = LazyApp && (
    <Suspense fallback={<Loading cover="content" />}>
      {/* key por path: al cambiar de app, la saliente se desmonta
          (dispara detachAndUnloadImmediate) antes de montar la nueva */}
      <LazyApp key={path} />
    </Suspense>
  );

  // ── Módulos de administración: modal claro, sin lienzo ni confirmación ──
  if (isTool) {
    return (
      <Modal
        key="tool"
        open={open}
        title={title}
        onCancel={() => setOpen(false)}
        afterClose={clearOpenAppContext}
        footer={null}
        centered
        width={1100}
        destroyOnClose
        className="app-popup-tool-modal"
        styles={{ body: { maxHeight: "72vh", overflowY: "auto" } }}
      >
        {body}
      </Modal>
    );
  }

  // Pantalla completa dentro del popup. Unity: el canvas, como siempre. Módulos sin
  // canvas (los tours son un iframe): el stage entero, que en :fullscreen centra el
  // contenido en 16:9 con bandas negras (index.css). Si se pusiera el iframe solo,
  // se estiraría a la forma de la pantalla y quedarían botones del tour fuera.
  const handleFullscreen = () => {
    const target =
      document.querySelector(".app-popup-stage canvas") ||
      document.querySelector(".app-popup-stage");
    if (target && target.requestFullscreen) target.requestFullscreen().catch(() => {});
  };

  // Avisa a la plataforma que la actividad se cerró, para que refresque el
  // progreso (Home escucha 'appClosed'). Se emite en afterClose, es decir
  // después de que destroyOnClose desmontó la instancia de Unity.
  const handleAfterClose = () => {
    clearOpenAppContext();
    window.dispatchEvent(new CustomEvent("appClosed", { detail: { path, title } }));
  };

  // El tutorial se abre aparte para no interrumpir la simulación en curso.
  const handleTutorial = () => {
    if (!tutorial) return;
    window.open(tutorial, "_blank", "noopener,noreferrer");
  };

  // Cierre con confirmación (se pierde el progreso interno de la simulación)
  const requestClose = () => {
    Modal.confirm({
      title: "¿Salir de la actividad?",
      content: "Se perderá el progreso no guardado de la simulación.",
      okText: "Salir",
      cancelText: "Seguir",
      okButtonProps: { danger: true },
      onOk: () => setOpen(false),
    });
  };

  return (
    <Modal
      key="unity"
      open={open}
      onCancel={requestClose}
      afterClose={handleAfterClose}
      footer={null}
      closable={false}
      centered
      width={dims.w || "96vw"}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      className="app-popup-modal"
      styles={{
        content: { padding: 0, overflow: "hidden" },
        body: {
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: "#0a0e1a",
        },
      }}
    >
      {/* Header: nombre de la actividad + cerrar */}
      <div className="app-popup-header">
        <div>
          <div className="app-popup-header-overline">
            {appModule?.kind === "tour" ? "Tour virtual" : "Simulador · Unity WebGL"}
          </div>
          <div className="app-popup-header-title">{title}</div>
        </div>
        <button className="app-popup-close" onClick={requestClose} aria-label="Cerrar">
          <CloseOutlined />
        </button>
      </div>

      {/* Stage 16:9 */}
      {LazyApp && (
        <div className="app-popup-stage" style={{ height: dims.h || "auto" }}>
          {body}
        </div>
      )}

      {/* Footer: acciones */}
      <div className="app-popup-footer">
        <span className="app-popup-footer-hint" />
        <div className="app-popup-footer-actions">
          <Tooltip
            title={
              tutorial
                ? "Abrir el tutorial de la actividad en una pestaña nueva"
                : "Esta actividad no tiene tutorial disponible"
            }
          >
            {/* span: un botón deshabilitado no emite los eventos del Tooltip */}
            <span>
              <Button
                className="app-popup-help-btn"
                icon={<QuestionCircleOutlined />}
                disabled={!tutorial}
                onClick={handleTutorial}
              >
                Ayuda
              </Button>
            </span>
          </Tooltip>
          <Button className="app-popup-report-btn" icon={<WarningOutlined />} onClick={goToBugURL}>
            Reportar Error
          </Button>
          {/* Safari de iPhone sólo permite pantalla completa en <video>: ahí el
              botón no haría nada. iPad sí la soporta y lo conserva. */}
          {document.fullscreenEnabled && (
            <Button type="primary" icon={<FullscreenOutlined />} onClick={handleFullscreen}>
              Pantalla Completa
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
